import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { initPixel, trackLead } from "@/lib/metaPixel";
import api from "@/lib/api";
import PublicField from "@/components/forms/PublicField";
import Ending from "@/components/forms/Ending";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  DEFAULT_LABELS, DEFAULT_THEME, endingsOf, logicTarget, computeScore, computeVars, topIndexOf, optionsFor, recall, assetUrl, SCALE_TYPES, meetingVarsFor,
} from "@/lib/forms";
import { ChevronUp, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "fbclid", "gclid", "ttclid", "msclkid"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;
const LOCK = {
  url: [(v) => URL_RE.test(v), "Please enter a valid link, e.g. https://example.com"],
  email: [(v) => EMAIL_RE.test(v), null],
  phone: [(v) => v.replace(/\D/g, "").length >= 7 && /^[+\d\s()-]+$/.test(v), null],
  number: [(v) => v !== "" && !Number.isNaN(Number(v)), "Please enter a number"],
  letters: [(v) => /^[\p{L}\s.'-]+$/u.test(v), "Please use letters only"],
};
const SCREENS = ["welcome", "statement"];
const RADIUS = { none: "rounded-none", md: "rounded-md", pill: "rounded-full" };
const AUTO_ADVANCE = ["single_choice", "yes_no", "dropdown", "picture_choice", "rating", "opinion_scale", "nps", "consent"];

const questionsOf = (step) => (step.type === "group" ? step.children || [] : SCREENS.includes(step.type) ? [] : [step]);

function validate(q, v, L) {
  const empty = v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length) || (q.type === "consent" && v === false);
  if (empty) return q.required ? L.required : null;
  if (q.type === "email" && !EMAIL_RE.test(String(v).trim())) return L.email_invalid;
  if (q.type === "phone" && String(v).replace(/\D/g, "").length < 7) return L.phone_invalid;
  if (q.type === "url" && !URL_RE.test(String(v).trim())) return LOCK.url[1];
  const lock = LOCK[q.answer_type];
  if (lock && !lock[0](String(v).trim())) return lock[1] || (q.answer_type === "email" ? L.email_invalid : L.phone_invalid);
  return null;
}

function useFont(font) {
  useEffect(() => {
    if (!font || font === "Geist") return;
    const id = `font-${font.replace(/\s/g, "-")}`;
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id; l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${font.replace(/\s/g, "+")}:wght@400;500;600&display=swap`;
    document.head.appendChild(l);
  }, [font]);
}

export default function FormRunner({ form, preview = false, startAt = null, embedded = false }) {
  const steps = useMemo(() => form.blocks || [], [form]);
  const endings = useMemo(() => endingsOf(form), [form]);
  const theme = { ...DEFAULT_THEME, ...(form.settings?.theme || {}) };
  const L = useMemo(() => ({ ...DEFAULT_LABELS, ...(form.settings?.labels || {}) }), [form]);
  const accent = form.settings?.accent_color || "#ffffff";
  const radius = RADIUS[theme.button_radius] || RADIUS.md;
  useFont(theme.font);

  const startIdx = Math.max(0, startAt ? topIndexOf(steps, startAt) : 0);
  const startEnding = startAt ? endings.find((e) => e.id === startAt) : null;
  const [idx, setIdx] = useState(startIdx);
  const [history, setHistory] = useState([]);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({});
  const ctx = useMemo(() => ({ ...answers, ...computeVars(form, answers) }), [form, answers]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const sampleMeeting = () => {
    const cal = steps.flatMap((s) => questionsOf(s)).find((q) => ["calendlo", "calendar_meet"].includes(q.type));
    if (!cal) return null;
    const raw = answers?.[cal.id];
    return { start: (typeof raw === "string" && raw) || new Date(Date.now() + 86400000).toISOString(), duration_min: cal.meeting_duration_min || 30,
      title: cal.event_name || "Call", meet_url: "https://meet.google.com/preview", confirmed: true };
  };
  const [done, setDone] = useState(startEnding ? { ending: startEnding, meeting: preview ? sampleMeeting() : null } : null);
  const [slotReload, setSlotReload] = useState(0);
  const sessionId = useRef(`s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`);

  const hidden = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const h = {};
    [...UTM, ...(form.settings?.hidden_fields || [])].forEach((k) => { if (p.get(k)) h[k] = p.get(k); });
    if (!h.fbclid) {
      const fb = new URLSearchParams(window.location.hash.split("?")[1] || "").get("fbclid") || sessionStorage.getItem("tt_fbclid");
      if (fb) h.fbclid = fb;
    }
    if (h.fbclid) sessionStorage.setItem("tt_fbclid", h.fbclid);
    h.page_url = window.location.href;
    if (document.referrer) h.referrer = document.referrer;
    const ck = (n) => (document.cookie.match(new RegExp(`(?:^|; )${n}=([^;]*)`)) || [])[1];
    if (ck("_fbp")) h.fbp = decodeURIComponent(ck("_fbp"));
    if (ck("_fbc")) h.fbc = decodeURIComponent(ck("_fbc"));
    return h;
  }, [form]);

  const step = steps[idx];
  const qNum = useMemo(() => {
    let n = 0; const m = {};
    steps.forEach((s) => questionsOf(s).forEach((q) => { m[q.id] = ++n; }));
    return m;
  }, [steps]);
  const setAns = (id, v) => { setAnswers((a) => ({ ...a, [id]: v })); setErrors((e) => ({ ...e, [id]: null })); };
  const rootRef = useRef(null);
  useEffect(() => { if (!preview) initPixel(form.settings?.meta_pixel_id); }, [form.settings?.meta_pixel_id, preview]);
  useEffect(() => {
    if (embedded) rootRef.current?.querySelector(".form-scroll")?.scrollTo(0, 0);
    else window.scrollTo(0, 0);
  }, [idx, done, embedded]);

  const finish = useCallback(async (ending) => {
    if (preview) {
      setDone({ ending, meeting: sampleMeeting() });
      return;
    }
    const path = [...history, idx].map((i) => steps[i]?.id).filter(Boolean);
    setBusy(true);
    try {
      const { data } = await api.post(`/public/forms/${form.slug}/submit`, {
        answers, hidden, user_agent: navigator.userAgent, path, ending_id: ending.id, session_id: sessionId.current,
      });
      setDone({ ending: endings.find((e) => e.id === data.ending_id) || ending, meeting: data.meeting });
      trackLead(form.settings?.meta_pixel_id, data.lead_id);
    } catch (e) {
      if (e.response?.status === 409) {
        const ci = steps.findIndex((s) => s.type === "calendar_meet" || s.type === "calendlo");
        if (ci >= 0) { setAnswers((a) => ({ ...a, [steps[ci].id]: null })); setSlotReload((k) => k + 1); setDir(-1); setIdx(ci); }
      }
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  }, [answers, hidden, form.slug, form.settings, steps, endings, history, idx, preview]);

  const next = useCallback(() => {
    if (!step || busy || done) return;
    const errs = {};
    questionsOf(step).forEach((q) => { const m = validate(q, answers[q.id], L); if (m) errs[q.id] = m; });
    if (Object.keys(errs).length) { setErrors((e) => ({ ...e, ...errs })); return; }
    const target = logicTarget(step, answers, computeScore(steps, answers), computeVars(form, answers));
    const endingHit = target && endings.find((e) => e.id === target);
    if (!preview && !endingHit && questionsOf(step).length && idx < steps.length - 1) api.post(`/public/forms/${form.slug}/partial`, { session_id: sessionId.current, answers, hidden, last_block: questionsOf(step).at(-1)?.id }).catch(() => {});
    if (endingHit) { finish(endingHit); return; }
    const ti = target ? topIndexOf(steps, target) : -1;
    const ni = ti > idx ? ti : idx + 1;
    if (ni >= steps.length) { finish(endings.find((e) => e.outcome !== "disqualified") || endings[0]); return; }
    setHistory((h) => [...h, idx]); setDir(1); setIdx(ni);
  }, [step, answers, idx, steps, endings, finish, busy, done, L, form]);

  const prev = () => {
    if (done || !history.length) return;
    setDir(-1); setIdx(history[history.length - 1]); setHistory((h) => h.slice(0, -1));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (done || !step) return;
      const tag = e.target.tagName;
      if (embedded && !e.target.closest?.(".public-form")) return;
      if (e.key === "Enter" && !e.shiftKey && step.type !== "calendar_meet" && step.type !== "calendlo" && (step.type !== "calendly" || answers[step.id])) { e.preventDefault(); next(); return; }
      const choice = ["single_choice", "yes_no", "multi_choice", "picture_choice"].includes(step.type);
      if (choice && tag !== "INPUT" && tag !== "TEXTAREA" && /^[a-z]$/i.test(e.key)) {
        const o = optionsFor(step)[e.key.toUpperCase().charCodeAt(0) - 65];
        if (!o) return;
        if (step.type === "multi_choice") { const cur = answers[step.id] || []; setAns(step.id, cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]); }
        else setAns(step.id, o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, next, done, answers, embedded]);

  // auto-advance after a single pick (runs after state commit so logic sees the new answer)
  const [autoAdv, setAutoAdv] = useState(0);
  useEffect(() => {
    if (!autoAdv) return undefined;
    const t = setTimeout(() => next(), 350);
    return () => clearTimeout(t);
  }, [autoAdv]); // eslint-disable-line react-hooks/exhaustive-deps
  const booked = step && !done && !busy && questionsOf(step).some((q) => q.type === "calendly" && answers[q.id]);
  useEffect(() => {
    if (!booked || preview) return undefined;
    const t = setTimeout(() => next(), 400);
    return () => clearTimeout(t);
  }, [booked]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPick = (q, v) => {
    setAns(q.id, v);
    if (step.type !== "group" && AUTO_ADVANCE.includes(q.type) && !(q.allow_other && !optionsFor(q).includes(v))) setAutoAdv((k) => k + 1);
  };

  if (!step && !done) return <div className="p-8 text-sm opacity-60">This form has no questions yet.</div>;
  const isLastLinear = idx === steps.length - 1;
  const flatQs = steps.flatMap(questionsOf);
  const ansOf = (fn) => answers[(flatQs.find(fn) || {}).id];
  const prefill = {
    name: ansOf((q) => q.maps_to === "name" || /^(full )?name$|your name/i.test(q.label || "")),
    firstName: ansOf((q) => q.maps_to === "name" || q.maps_to === "first_name"),
    lastName: ansOf((q) => q.maps_to === "last_name"),
    email: ansOf((q) => q.type === "email"),
    phone: ansOf((q) => q.type === "phone" || q.maps_to === "phone"),
  };
  const progress = done ? 100 : Math.round((idx / Math.max(steps.length, 1)) * 100);
  const btnLabel = SCREENS.includes(step?.type) ? step.button_label || (step.type === "welcome" ? "Start" : "Continue")
    : step?.button_label || (isLastLinear ? (busy ? "Submitting…" : L.submit) : L.next);
  const pos = embedded ? "absolute" : "fixed";
  const center = theme.align === "center";
  const logo = theme.logo_url && (
    <img src={assetUrl(theme.logo_url)} alt="logo" style={{ height: Number(theme.logo_height) || 28 }}
      className={`absolute top-5 z-20 ${theme.logo_position === "center" ? "left-1/2 -translate-x-1/2" : "left-6 sm:left-10"}`} data-testid="form-logo" />
  );

  return (
    <div ref={rootRef} className={`public-form relative overflow-hidden ${embedded ? "h-full" : "min-h-screen"}`} data-testid="public-form"
      style={{ background: theme.bg_color, color: theme.text_color, fontFamily: `"${theme.font}", Geist, sans-serif` }}>
      {theme.bg_image_url && (
        <>
          <div className={`${pos} inset-0 bg-cover bg-center`} style={{ backgroundImage: `url(${assetUrl(theme.bg_image_url)})` }} />
          <div className={`${pos} inset-0`} style={{ background: theme.bg_color, opacity: Number(theme.bg_overlay ?? 0.5) }} />
        </>
      )}
      {logo}
      {form.settings?.show_progress !== false && (
        <div className={`${pos} top-0 left-0 right-0 h-1 z-20`} style={{ background: `${theme.text_color}12` }}>
          <div className="h-full transition-[width] duration-500" style={{ width: `${progress}%`, background: accent }} data-testid="form-progress" />
        </div>
      )}
      <div className={`relative z-10 flex px-5 sm:px-12 lg:px-[12vw] ${theme.logo_url ? "" : "pt-16"} pb-28 sm:py-20 ${embedded ? "h-full overflow-y-auto form-scroll" : "min-h-[100dvh]"} ${center ? "justify-center text-center" : ""}`}
        style={theme.logo_url ? { paddingTop: (Number(theme.logo_height) || 28) + 44 } : undefined}>
        <div className="w-full max-w-2xl min-w-0 my-auto break-words">
          {done ? <Ending ending={(preview && endings.find((e) => e.id === done.ending?.id)) || done.ending} meeting={done.meeting} answers={{ ...ctx, ...meetingVarsFor(done.meeting?.start) }} accent={accent} radius={radius} preview={preview} /> : (
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div key={step.id} initial={{ opacity: 0, y: dir * 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: dir * -40 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} data-testid={`step-${step.id}`}>
                <ErrorBoundary message="This step couldn't load." onReset={() => setSlotReload((k) => k + 1)}>
                  {step.image_url && <img src={assetUrl(step.image_url)} alt="" className={`max-h-60 rounded-md mb-6 object-cover ${center ? "mx-auto" : ""}`} />}
                  {SCREENS.includes(step.type) && (
                    <>
                      <h1 className={`${step.type === "welcome" ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"} font-medium tracking-tight leading-tight`}>{recall(step.label, ctx)}</h1>
                      {step.description && <p className="opacity-70 text-lg mt-4 whitespace-pre-line">{recall(step.description, ctx)}</p>}
                    </>
                  )}
                  {step.type === "group" && (
                    <div className="mb-6">
                      <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">{recall(step.label, ctx)}</h2>
                      {step.description && <p className="opacity-70 mt-2">{recall(step.description, ctx)}</p>}
                    </div>
                  )}
                  <div className={step.type === "group" ? "space-y-8" : ""}>
                    {questionsOf(step).map((q, qi) => (
                      <div key={q.id} data-testid={`question-${q.id}`}>
                        {q.image_url && step.type === "group" && <img src={assetUrl(q.image_url)} alt="" className="max-h-40 rounded-md mb-3 object-cover" />}
                        <div className={`flex items-baseline gap-2 mb-1 ${center ? "justify-center" : ""}`}>
                          <span className="text-sm font-mono" style={{ color: accent }}>{qNum[q.id]}→</span>
                          <h2 className={`${step.type === "group" ? "text-lg sm:text-xl" : "text-2xl sm:text-3xl"} font-medium tracking-tight`}>
                            {recall(q.label, ctx)}{q.required && <span style={{ color: accent }}> *</span>}
                          </h2>
                        </div>
                        {q.description && <p className={`opacity-60 mb-4 ${center ? "" : "sm:ml-7"} whitespace-pre-line`}>{recall(q.description, ctx)}</p>}
                        <div className={`${center ? "flex justify-center" : "sm:ml-7"} mt-4 min-w-0`}>
                          <div className={center && !SCALE_TYPES.includes(q.type) ? "w-full max-w-md text-left" : "w-full"}>
                            <PublicField b={q} value={answers[q.id]} onChange={(v) => onPick(q, v)} accent={accent} slug={form.slug}
                              autoFocus={qi === 0 && !embedded} reloadKey={slotReload} labels={L} radius={radius} preview={preview} prefill={prefill} vars={ctx} />
                          </div>
                        </div>
                        {errors[q.id] && <div className={`mt-2 text-sm text-[#ef4444] ${center ? "" : "sm:ml-7"}`} data-testid={`error-${q.id}`}>{errors[q.id]}</div>}
                      </div>
                    ))}
                  </div>
                  <div className={`flex items-center gap-3 mt-8 ${center ? "justify-center" : SCREENS.includes(step.type) || step.type === "group" ? "" : "sm:ml-7"}`}>
                    <button onClick={next} disabled={busy} data-testid="form-next-btn"
                      className={`inline-flex items-center gap-2 px-5 py-2.5 font-medium text-black transition-transform active:scale-95 disabled:opacity-60 ${radius}`} style={{ background: accent }}>
                      {btnLabel}{!isLastLinear && <ArrowRight size={16} />}
                    </button>
                    {!["calendar_meet", "calendlo"].includes(step.type) && L.enter_hint && <span className="text-xs opacity-50 hidden sm:inline">{L.enter_hint}</span>}
                  </div>
                </ErrorBoundary>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
      {!done && (
        <div className={`${embedded ? "absolute" : "absolute sm:fixed"} bottom-5 right-5 flex overflow-hidden z-20 ${radius}`} style={{ background: accent }}>
          <button onClick={prev} disabled={!history.length} className="p-2 text-black disabled:opacity-40" data-testid="form-prev-arrow"><ChevronUp size={18} /></button>
          <div className="w-px bg-black/20" />
          <button onClick={next} disabled={busy} className="p-2 text-black" data-testid="form-next-arrow"><ChevronDown size={18} /></button>
        </div>
      )}
    </div>
  );
}
